import { Head, Link, Form as InertiaForm } from '@inertiajs/react'
import { TaxPayerType } from './types'
import {Button} from "@/components/ui/button.tsx";
import {
  AlertCircle,
  ArrowLeft,
  Building2, Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Edit,
  FileText,
  Mail,
  MapPin,
  Phone
} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useEffect, useState } from 'react'
import {Tabs, TabsContent, TabsTrigger} from "@/components/ui/tabs";
import {TabsList} from "@/components/ui/tabs.tsx";
import { Badge } from "@/components/ui/badge";

interface ShowProps {
  taxpayer: TaxPayerType
  tax_rules: {
    id: string
    name: string
    code?: string
    status: string
    formula_type: string
    logic_data?: Record<string, unknown>
  }[]
  assessments: {
    id: string
    code: string
    status: string
    period_name?: string
    due_date?: string
    total_amount: string
    balance_due: string
    tax_rule_id: string
    created_at: string
    display_name:string
  }[]
}

const statusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'partially_paid', label: 'Partially paid' },
  { value: 'paid', label: 'Paid' },
] as const

export default function Show({ taxpayer, tax_rules, assessments }: ShowProps) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [status, setStatus] = useState('pending')
  const [taxRuleId, setTaxRuleId] = useState(tax_rules[0]?.id || '')
  const [baseAmount, setBaseAmount] = useState('')
  const [sizeValue, setSizeValue] = useState('')

  useEffect(() => {
    if (!tax_rules.find((rule) => rule.id === taxRuleId)) {
      setTaxRuleId(tax_rules[0]?.id || '')
    }
  }, [taxRuleId, tax_rules])

  useEffect(() => {
    setBaseAmount('')
    setSizeValue('')
  }, [taxRuleId])

  const selectedRule = tax_rules.find((rule) => rule.id === taxRuleId)
  const selectedLogic = selectedRule?.logic_data || {}

  const parseRate = (value: unknown) => {
    if (value === null || value === undefined) return null
    const numeric = Number(value)
    return Number.isFinite(numeric) ? numeric : null
  }

  const percentageRate = () => {
    const rate = parseRate((selectedLogic as { rate?: unknown }).rate)
    if (rate !== null) return rate
    const label = String((selectedLogic as { label?: unknown }).label || '')
    const match = label.match(/([0-9]+(?:\.[0-9]+)?)/)
    return match ? Number(match[1]) : null
  }

  const totalAmount = (() => {
    if (!selectedRule) return null
    if (selectedRule.formula_type === 'flat_rate') {
      return parseRate((selectedLogic as { amount?: unknown }).amount)
    }
    if (selectedRule.formula_type === 'percentage') {
      const rate = percentageRate()
      const base = Number(baseAmount)
      if (!rate || !Number.isFinite(base)) return null
      return Number((base * rate / 100).toFixed(2))
    }
    if (selectedRule.formula_type === 'size_based') {
      const rate = parseRate((selectedLogic as { min_amount?: unknown }).min_amount)
      const size = Number(sizeValue)
      if (!rate || !Number.isFinite(size)) return null
      return Number((size * rate).toFixed(2))
    }
    return null
  })()

  return (
    <>
      <Head title={`Tax payer #${taxpayer.id}`} />
      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/tax_payers">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{taxpayer.name}</h1>
              <p className="text-sm text-muted-foreground">Taxpayer ID: {taxpayer.code}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link href={`/tax_payers/${taxpayer.id}/collect`}>
              <Button className="gap-2">
                <DollarSign className="h-4 w-4" />
                Collect Payment
              </Button>
            </Link>
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              <FileText className="h-4 w-4" />
              New Assessment
            </Button>
            <Link href={`/tax_payers/${taxpayer.id}/edit`}>
              <Button variant="outline" className="gap-2 bg-transparent">
                <Edit className="h-4 w-4" />
                Edit
              </Button>
            </Link>
          </div>
        </div>
        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Outstanding Balance</p>
                <p className="mt-2 text-2xl font-bold text-warning">0</p>
              </div>
              <AlertCircle className="h-8 w-8 text-warning" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Paid</p>
                <p className="mt-2 text-2xl font-bold text-success">$0</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Active Taxes</p>
                <p className="mt-2 text-2xl font-bold text-foreground">0</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Status</p>
                <Badge className="mt-2" variant={taxpayer.status === "overdue" ? "destructive" : "default"}>
                  {taxpayer.status}
                </Badge>
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Contact & Location */}
          <div className="space-y-6">
            <Card className="border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">Contact Information</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Phone</p>
                    <p className="font-medium text-foreground">{taxpayer.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium text-foreground">{taxpayer.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Adresse</p>
                    <p className="font-medium text-foreground">{taxpayer.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Zone</p>
                    <p className="font-medium text-foreground">{taxpayer.area?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Categorie</p>
                    <p className="font-medium text-foreground">{taxpayer.category?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Registered</p>
                    <p className="font-medium text-foreground">
                      {/*{new Date(taxpayer.registeredDate).toLocaleDateString()}*/}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">GPS Location</h3>
              <div className="relative h-[300px] overflow-hidden rounded-lg border border-border bg-secondary">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <MapPin className="mx-auto mb-2 h-12 w-12 text-primary" />
                    <p className="text-sm text-muted-foreground">Property Location</p>
                    <p className="mt-2 font-mono text-xs text-muted-foreground">
                      {taxpayer.latitude}° N, {taxpayer.longitude}° E
                    </p>
                    <Button size="sm" variant="outline" className="mt-4 bg-transparent">
                      View on Map
                    </Button>
                  </div>
                </div>
                <div className="absolute left-1/2 top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 rounded-full bg-destructive ring-4 ring-destructive/20"></div>
              </div>
            </Card>
          </div>

          {/* Right Column - Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="taxes" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="taxes">Tax Assignments</TabsTrigger>
                <TabsTrigger value="payments">Payment History</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="taxes" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Assigned Taxes</h3>
                    <Button size="sm" variant="outline">
                      Assign New Tax
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {assessments.map((tax) => (
                        <div key={tax.id} className="rounded-lg border border-border p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-foreground">{tax.display_name}</p>
                                <Badge
                                    variant={
                                      tax.status === "paid"
                                          ? "default"
                                          : tax.status === "pending"
                                              ? "destructive"
                                              : "secondary"
                                    }
                                >
                                  {tax.status}
                                </Badge>
                              </div>
                              <p className="mt-1 text-sm text-muted-foreground">{tax.code}</p>
                              <div className="mt-2 flex items-center gap-4 text-sm">
                            <span className="text-muted-foreground">
                              Due: {new Date(tax.due_date!).toLocaleDateString()}
                            </span>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-2xl font-bold text-foreground">${tax.total_amount}</p>
                            </div>
                          </div>
                        </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="payments" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">Payment History</h3>
                  <div className="space-y-3">
                    {/*{paymentHistory.map((payment) => (*/}
                    {/*    <div key={payment.id} className="rounded-lg border border-border p-4">*/}
                    {/*      <div className="flex items-start justify-between">*/}
                    {/*        <div className="flex-1">*/}
                    {/*          <div className="flex items-center gap-2">*/}
                    {/*            <CreditCard className="h-4 w-4 text-muted-foreground" />*/}
                    {/*            <p className="font-semibold text-foreground">{payment.taxType}</p>*/}
                    {/*          </div>*/}
                    {/*          <p className="mt-1 font-mono text-xs text-muted-foreground">{payment.id}</p>*/}
                    {/*          <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">*/}
                    {/*            <span>{new Date(payment.date).toLocaleDateString()}</span>*/}
                    {/*            <span>•</span>*/}
                    {/*            <span>{payment.method}</span>*/}
                    {/*            <span>•</span>*/}
                    {/*            <span>by {payment.agent}</span>*/}
                    {/*          </div>*/}
                    {/*        </div>*/}
                    {/*        <div className="text-right">*/}
                    {/*          <p className="text-xl font-bold text-success">${payment.amount}</p>*/}
                    {/*          <Badge variant="default" className="mt-1">*/}
                    {/*            {payment.status}*/}
                    {/*          </Badge>*/}
                    {/*        </div>*/}
                    {/*      </div>*/}
                    {/*    </div>*/}
                    {/*))}*/}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="documents" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Documents</h3>
                    <Button size="sm" variant="outline">
                      Upload Document
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {/*{documents.map((doc) => (*/}
                    {/*    <div key={doc.id} className="flex items-center justify-between rounded-lg border border-border p-4">*/}
                    {/*      <div className="flex items-center gap-3">*/}
                    {/*        <FileText className="h-8 w-8 text-primary" />*/}
                    {/*        <div>*/}
                    {/*          <p className="font-medium text-foreground">{doc.name}</p>*/}
                    {/*          <p className="text-sm text-muted-foreground">*/}
                    {/*            {doc.type} • {doc.size} • Uploaded {new Date(doc.uploadDate).toLocaleDateString()}*/}
                    {/*          </p>*/}
                    {/*        </div>*/}
                    {/*      </div>*/}
                    {/*      <Button size="sm" variant="outline">*/}
                    {/*        Download*/}
                    {/*      </Button>*/}
                    {/*    </div>*/}
                    {/*))}*/}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>


      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New assessment</DialogTitle>
          </DialogHeader>
          <InertiaForm
            transform={(data) => ({ assessment: data })}
            method="post"
            action={`/tax_payers/${taxpayer.id}/assessments`}
            className="space-y-4"
            onSuccess={() => setDialogOpen(false)}
          >
            {({ errors, processing }) => (
              <>
                <div>
                  <Label htmlFor="tax_rule_id">Tax rule</Label>
                  <input type="hidden" name="tax_rule_id" value={taxRuleId} />
                  <Select value={taxRuleId} onValueChange={setTaxRuleId}>
                    <SelectTrigger id="tax_rule_id">
                      <SelectValue placeholder="Select a tax rule" />
                    </SelectTrigger>
                    <SelectContent>
                      {tax_rules.map((rule) => (
                        <SelectItem key={rule.id} value={rule.id}>
                          {rule.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.tax_rule_id && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.tax_rule_id.join(', ')}
                    </div>
                  )}
                </div>
                {selectedRule?.formula_type === 'percentage' && (
                  <div>
                    <Label htmlFor="base_amount">Base amount</Label>
                    <Input
                      id="base_amount"
                      name="base_amount"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={baseAmount}
                      onChange={(event) => setBaseAmount(event.target.value)}
                    />
                  </div>
                )}
                {selectedRule?.formula_type === 'size_based' && (
                  <div>
                    <Label htmlFor="size_value">Size value</Label>
                    <Input
                      id="size_value"
                      name="size_value"
                      type="number"
                      step="0.01"
                      placeholder="0"
                      value={sizeValue}
                      onChange={(event) => setSizeValue(event.target.value)}
                    />
                  </div>
                )}
                <div>
                  <Label htmlFor="total_amount">Total amount</Label>
                  <Input
                    id="total_amount"
                    type="text"
                    value={totalAmount === null ? '-' : totalAmount}
                    readOnly
                  />
                  {errors.total_amount && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.total_amount.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="period_name">Period name</Label>
                  <Input
                    id="period_name"
                    name="period_name"
                    placeholder="Jan 2026"
                  />
                  {errors.period_name && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.period_name.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="due_date">Due date</Label>
                  <Input id="due_date" name="due_date" type="date" />
                  {errors.due_date && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.due_date.join(', ')}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <input type="hidden" name="status" value={status} />
                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger id="status">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.status && (
                    <div className="text-red-500 px-3 py-2 font-medium">
                      {errors.status.join(', ')}
                    </div>
                  )}
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={processing}>
                    Create assessment
                  </Button>
                </div>
              </>
            )}
          </InertiaForm>
        </DialogContent>
      </Dialog>
    </>
  )
}
